import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { PathLayer, PolygonLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { PickingInfo, Layer as DeckLayer } from '@deck.gl/core';
import type { Point, LineString, Polygon, Position } from 'geojson';
import { useCollaborationStore } from '@/stores/collaborationStore';
import { useMapStore } from '@/stores/mapStore';
import type { OperationalFeature, Layer } from '@/types/operational';
import { createCircleCoordinates, sampleBallisticDots } from '@/lib/drawing/geometry';

// ── Color helpers ────────────────────────────────────────────────

function hexToRgba(hex: string, alpha: number): [number, number, number, number] {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (r) return [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16), alpha];
  return [56, 189, 248, alpha]; // fallback sky-400
}

const SEL = [96, 165, 250] as const;   // selection blue (blue-400)
const WHITE = [255, 255, 255] as const;

function selOrColor(selected: boolean, hex: string, alpha: number): [number, number, number, number] {
  if (selected) return [SEL[0], SEL[1], SEL[2], alpha];
  return hexToRgba(hex, alpha);
}

// ── Hook ─────────────────────────────────────────────────────────

export function useDeckLayers() {
  const { getAnnotations, getLayers, ydoc } = useCollaborationStore();
  const { selection, toggleSelection, drawingPreview, drawingStyle, selectionBox } = useMapStore();
  const [annotationsData, setAnnotationsData] = useState<OperationalFeature[]>([]);
  const [layersData, setLayersData] = useState<Layer[]>([]);

  const phaseRef = useRef(0);
  const [dotTick, setDotTick] = useState(0);

  const selectedIds = useMemo(() => new Set(selection.featureIds), [selection.featureIds]);

  // Yjs subscriptions
  useEffect(() => {
    if (!ydoc) return;
    const annotations = getAnnotations();
    if (!annotations) return;
    setAnnotationsData(annotations.toArray());
    const obs = () => setAnnotationsData(annotations.toArray());
    annotations.observe(obs);
    return () => annotations.unobserve(obs);
  }, [ydoc, getAnnotations]);

  useEffect(() => {
    if (!ydoc) return;
    const layers = getLayers();
    if (!layers) return;
    setLayersData(layers.toArray());
    const obs = () => setLayersData(layers.toArray());
    layers.observe(obs);
    return () => layers.unobserve(obs);
  }, [ydoc, getLayers]);

  // Ballistic animation — ~7fps interval
  const hasBallisticFeatures = useMemo(
    () =>
      annotationsData.some((f) => f.properties?.featureType === 'ballistic') ||
      drawingPreview?.type === 'ballistic',
    [annotationsData, drawingPreview?.type]
  );

  useEffect(() => {
    if (!hasBallisticFeatures) return;
    let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      phaseRef.current = (phaseRef.current + (now - last) * 0.00015) % 1;
      last = now;
      setDotTick((t) => t + 1);
    }, 150);
    return () => clearInterval(id);
  }, [hasBallisticFeatures]);

  const layerVisibilityMap = useMemo(() => {
    const m = new Map<string, boolean>();
    layersData.forEach((l) => m.set(l.id, l.visible));
    return m;
  }, [layersData]);

  const visibleAnnotations = useMemo(
    () => annotationsData.filter((f) => {
      const lid = f.properties?.layerId;
      return !lid || layerVisibilityMap.get(lid) !== false;
    }),
    [annotationsData, layerVisibilityMap]
  );

  const ballisticFeatures = useMemo(
    () => visibleAnnotations.filter((f) => f.properties?.featureType === 'ballistic'),
    [visibleAnnotations]
  );

  // ── STATIC LAYERS ──────────────────────────────────────────────
  const staticLayers = useMemo(() => {
    const out: DeckLayer[] = [];

    // Selection box
    if (selectionBox) {
      out.push(new PolygonLayer({
        id: 'selection-box',
        data: [{ polygon: [
          [selectionBox.startLng, selectionBox.startLat],
          [selectionBox.endLng, selectionBox.startLat],
          [selectionBox.endLng, selectionBox.endLat],
          [selectionBox.startLng, selectionBox.endLat],
          [selectionBox.startLng, selectionBox.startLat],
        ] as [number, number][] }],
        getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
        getFillColor: [SEL[0], SEL[1], SEL[2], 20],
        getLineColor: [SEL[0], SEL[1], SEL[2], 160],
        getLineWidth: 1,
        lineWidthMinPixels: 1,
        stroked: true,
        filled: true,
      }));
    }

    // ── DRAWING PREVIEW ──────────────────────────────────────────
    if (drawingPreview && drawingPreview.coordinates.length > 0) {
      const pc = hexToRgba(drawingStyle.color, 255);
      const pcGlow = hexToRgba(drawingStyle.color, 30);
      const pcMid = hexToRgba(drawingStyle.color, 140);

      if (drawingPreview.type === 'line') {
        // Glow
        out.push(new PathLayer({
          id: 'preview-line-glow',
          data: [{ path: drawingPreview.coordinates }],
          getPath: (d: { path: [number, number][] }) => d.path,
          getColor: pcGlow,
          getWidth: 14,
          widthMinPixels: 6,
          capRounded: true,
          jointRounded: true,
        }));
        // Core
        out.push(new PathLayer({
          id: 'preview-line-core',
          data: [{ path: drawingPreview.coordinates }],
          getPath: (d: { path: [number, number][] }) => d.path,
          getColor: pc,
          getWidth: drawingStyle.strokeWidth,
          widthMinPixels: 2,
          capRounded: true,
          jointRounded: true,
        }));
        // Vertex dots
        out.push(new ScatterplotLayer({
          id: 'preview-line-verts',
          data: drawingPreview.coordinates,
          getPosition: (d: [number, number]) => d,
          getRadius: 5,
          getFillColor: [WHITE[0], WHITE[1], WHITE[2], 255],
          getLineColor: pc,
          getLineWidth: 2,
          stroked: true,
          radiusMinPixels: 4,
          radiusMaxPixels: 8,
        }));

      } else if (drawingPreview.type === 'ballistic') {
        const si = drawingPreview.coordinates.findIndex((c) => c[0] === -999 && c[1] === -999);
        const traj = si !== -1 ? drawingPreview.coordinates.slice(0, si) : drawingPreview.coordinates;
        const impactCoords = si !== -1 ? drawingPreview.coordinates.slice(si + 1) : null;

        out.push(new PathLayer({
          id: 'preview-balli-glow', data: [{ path: traj }],
          getPath: (d: { path: [number, number][] }) => d.path,
          getColor: pcGlow, getWidth: 14, widthMinPixels: 6, capRounded: true, jointRounded: true,
        }));
        out.push(new PathLayer({
          id: 'preview-balli-arc', data: [{ path: traj }],
          getPath: (d: { path: [number, number][] }) => d.path,
          getColor: pcMid, getWidth: 2, widthMinPixels: 1, capRounded: true, jointRounded: true,
        }));
        if (traj.length > 0) {
          pushMarker(out, 'preview-balli-origin', [traj[0]], pc);
          pushMarker(out, 'preview-balli-target', [traj[traj.length - 1]], [255, 70, 70, 255]);
        }
        if (impactCoords && impactCoords.length > 0) {
          out.push(new PolygonLayer({
            id: 'preview-balli-impact',
            data: [{ polygon: impactCoords }],
            getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
            getFillColor: [255, 70, 70, 15],
            getLineColor: [255, 70, 70, 120],
            getLineWidth: 1.5, lineWidthMinPixels: 1, stroked: true, filled: true,
          }));
        }

      } else if (drawingPreview.type === 'polygon' || drawingPreview.type === 'rectangle' || drawingPreview.type === 'circle') {
        const pf = hexToRgba(drawingStyle.fillColor, Math.floor(drawingStyle.fillOpacity * 255));
        // Glow border
        out.push(new PolygonLayer({
          id: 'preview-poly-glow',
          data: [{ polygon: drawingPreview.coordinates }],
          getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
          getFillColor: [0, 0, 0, 0],
          getLineColor: pcGlow,
          getLineWidth: 12, lineWidthMinPixels: 5, stroked: true, filled: false,
        }));
        // Fill + stroke
        out.push(new PolygonLayer({
          id: 'preview-poly-core',
          data: [{ polygon: drawingPreview.coordinates }],
          getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
          getFillColor: pf,
          getLineColor: pc,
          getLineWidth: drawingStyle.strokeWidth, lineWidthMinPixels: 2, stroked: true, filled: true,
        }));
        // Vertex dots
        const verts = drawingPreview.type === 'polygon'
          ? drawingPreview.coordinates.slice(0, -1)
          : drawingPreview.coordinates;
        out.push(new ScatterplotLayer({
          id: 'preview-poly-verts',
          data: verts,
          getPosition: (d: [number, number]) => d,
          getRadius: 5,
          getFillColor: [WHITE[0], WHITE[1], WHITE[2], 255],
          getLineColor: pc,
          getLineWidth: 2,
          stroked: true,
          radiusMinPixels: 4,
          radiusMaxPixels: 8,
        }));
      }
    }

    // ── SAVED ANNOTATIONS ────────────────────────────────────────

    // Points — glow + white-border pin
    const pointFeatures = visibleAnnotations.filter((f) => f.geometry.type === 'Point');
    if (pointFeatures.length > 0) {
      // Outer glow
      out.push(new ScatterplotLayer({
        id: 'points-glow',
        data: pointFeatures,
        pickable: false,
        getPosition: (d: OperationalFeature) => (d.geometry as Point).coordinates as [number, number],
        getRadius: 18,
        getFillColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return selOrColor(s, d.properties?.style?.fillColor || '#38BDF8', 25);
        },
        radiusMinPixels: 12,
        radiusMaxPixels: 24,
        updateTriggers: { getFillColor: [selectedIds] },
      }));
      // White ring
      out.push(new ScatterplotLayer({
        id: 'points-ring',
        data: pointFeatures,
        pickable: true,
        getPosition: (d: OperationalFeature) => (d.geometry as Point).coordinates as [number, number],
        getRadius: 8,
        getFillColor: [0, 0, 0, 0],
        getLineColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return s ? [SEL[0], SEL[1], SEL[2], 255] : [WHITE[0], WHITE[1], WHITE[2], 200];
        },
        getLineWidth: 2.5,
        stroked: true,
        radiusMinPixels: 7,
        radiusMaxPixels: 14,
        updateTriggers: { getLineColor: [selectedIds] },
      }));
      // Inner fill
      out.push(new ScatterplotLayer({
        id: 'points-fill',
        data: pointFeatures,
        pickable: false,
        getPosition: (d: OperationalFeature) => (d.geometry as Point).coordinates as [number, number],
        getRadius: 5,
        getFillColor: (d: OperationalFeature) => hexToRgba(d.properties?.style?.fillColor || '#38BDF8', 230),
        radiusMinPixels: 4,
        radiusMaxPixels: 10,
      }));
    }

    // Lines — glow halo + core + vertex dots
    const lineFeatures = visibleAnnotations.filter(
      (f) => f.geometry.type === 'LineString' && f.properties?.featureType !== 'ballistic'
    );
    if (lineFeatures.length > 0) {
      // Glow
      out.push(new PathLayer({
        id: 'lines-glow',
        data: lineFeatures,
        pickable: false,
        getPath: (d: OperationalFeature) => (d.geometry as LineString).coordinates as [number, number][],
        getColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return selOrColor(s, d.properties?.style?.strokeColor || '#38BDF8', 25);
        },
        getWidth: 14,
        widthMinPixels: 6,
        capRounded: true,
        jointRounded: true,
        updateTriggers: { getColor: [selectedIds] },
      }));
      // Core
      out.push(new PathLayer({
        id: 'lines-core',
        data: lineFeatures,
        pickable: true,
        getPath: (d: OperationalFeature) => (d.geometry as LineString).coordinates as [number, number][],
        getColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return selOrColor(s, d.properties?.style?.strokeColor || '#38BDF8', 255);
        },
        getWidth: (d: OperationalFeature) => d.properties?.style?.strokeWidth || 2,
        widthMinPixels: 2,
        capRounded: true,
        jointRounded: true,
        updateTriggers: { getColor: [selectedIds] },
      }));
      // Vertex dots
      const vertexData = lineFeatures.flatMap((f) => {
        const coords = (f.geometry as LineString).coordinates;
        const color = hexToRgba(f.properties?.style?.strokeColor || '#38BDF8', 255);
        const s = selectedIds.has(f.properties?.id || '');
        const ring: [number, number, number, number] = s ? [SEL[0], SEL[1], SEL[2], 255] : [WHITE[0], WHITE[1], WHITE[2], 200];
        return coords.map((c) => ({ position: c as [number, number], color, ring }));
      });
      out.push(new ScatterplotLayer({
        id: 'lines-verts',
        data: vertexData,
        pickable: false,
        getPosition: (d) => d.position,
        getRadius: 4,
        getFillColor: (d) => d.color,
        getLineColor: (d) => d.ring,
        getLineWidth: 1.5,
        stroked: true,
        radiusMinPixels: 3,
        radiusMaxPixels: 7,
        updateTriggers: { getLineColor: [selectedIds] },
      }));
    }

    // Polygons — glow border + fill + stroke + corner dots
    const polyFeatures = visibleAnnotations.filter((f) => f.geometry.type === 'Polygon');
    if (polyFeatures.length > 0) {
      // Glow border
      out.push(new PolygonLayer({
        id: 'polys-glow',
        data: polyFeatures,
        pickable: false,
        getPolygon: (d: OperationalFeature) => (d.geometry as Polygon).coordinates[0] as [number, number][],
        getFillColor: [0, 0, 0, 0],
        getLineColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return selOrColor(s, d.properties?.style?.strokeColor || '#38BDF8', 20);
        },
        getLineWidth: 12,
        lineWidthMinPixels: 5,
        stroked: true,
        filled: false,
        updateTriggers: { getLineColor: [selectedIds] },
      }));
      // Fill + stroke
      out.push(new PolygonLayer({
        id: 'polys-core',
        data: polyFeatures,
        pickable: true,
        stroked: true,
        filled: true,
        extruded: false,
        getPolygon: (d: OperationalFeature) => (d.geometry as Polygon).coordinates[0] as [number, number][],
        getFillColor: (d: OperationalFeature) => {
          const o = d.properties?.style?.fillOpacity ?? 0.12;
          return hexToRgba(d.properties?.style?.fillColor || '#38BDF8', Math.floor(o * 255));
        },
        getLineColor: (d: OperationalFeature) => {
          const s = selectedIds.has(d.properties?.id || '');
          return selOrColor(s, d.properties?.style?.strokeColor || '#38BDF8', 200);
        },
        getLineWidth: (d: OperationalFeature) => d.properties?.style?.strokeWidth || 2,
        lineWidthMinPixels: 1,
        updateTriggers: { getLineColor: [selectedIds] },
      }));
      // Corner vertex dots
      const cornerData = polyFeatures.flatMap((f) => {
        const coords = (f.geometry as Polygon).coordinates[0];
        const color = hexToRgba(f.properties?.style?.strokeColor || '#38BDF8', 255);
        const s = selectedIds.has(f.properties?.id || '');
        const ring: [number, number, number, number] = s ? [SEL[0], SEL[1], SEL[2], 255] : [WHITE[0], WHITE[1], WHITE[2], 180];
        // Skip last coord (same as first for closed polygon)
        return coords.slice(0, -1).map((c: Position) => ({ position: c as [number, number], color, ring }));
      });
      if (cornerData.length > 0) {
        out.push(new ScatterplotLayer({
          id: 'polys-verts',
          data: cornerData,
          pickable: false,
          getPosition: (d) => d.position,
          getRadius: 4,
          getFillColor: (d) => d.color,
          getLineColor: (d) => d.ring,
          getLineWidth: 1.5,
          stroked: true,
          radiusMinPixels: 3,
          radiusMaxPixels: 7,
          updateTriggers: { getLineColor: [selectedIds] },
        }));
      }
    }

    // ── BALLISTIC STATIC ─────────────────────────────────────────
    if (ballisticFeatures.length > 0) {
      // Glow arc
      out.push(new PathLayer({
        id: 'balli-glow',
        data: ballisticFeatures, pickable: false,
        getPath: (d: OperationalFeature) => (d.geometry as LineString).coordinates as [number, number][],
        getColor: (d: OperationalFeature) => selOrColor(selectedIds.has(d.properties?.id || ''), d.properties?.style?.strokeColor || '#FF4444', 20),
        getWidth: 14, widthMinPixels: 6, capRounded: true, jointRounded: true,
        updateTriggers: { getColor: [selectedIds] },
      }));
      // Core arc
      out.push(new PathLayer({
        id: 'balli-arc',
        data: ballisticFeatures, pickable: true,
        getPath: (d: OperationalFeature) => (d.geometry as LineString).coordinates as [number, number][],
        getColor: (d: OperationalFeature) => selOrColor(selectedIds.has(d.properties?.id || ''), d.properties?.style?.strokeColor || '#FF4444', 90),
        getWidth: 2, widthMinPixels: 1, capRounded: true, jointRounded: true,
        updateTriggers: { getColor: [selectedIds] },
      }));

      // Origin markers
      const origins = ballisticFeatures.map((f) => ({
        position: [f.properties?.originLng as number, f.properties?.originLat as number] as [number, number],
        color: hexToRgba(f.properties?.style?.strokeColor || '#FF4444', 255),
      }));
      pushMarkerBatch(out, 'balli-origin', origins);

      // Impact zones
      const impactData = ballisticFeatures.map((f) => {
        const tLng = f.properties?.targetLng as number;
        const tLat = f.properties?.targetLat as number;
        const ir = f.properties?.impactRadius as number;
        return {
          feature: f,
          polygon: createCircleCoordinates([tLng, tLat], ir) as [number, number][],
          innerPolygon: createCircleCoordinates([tLng, tLat], ir * 0.45) as [number, number][],
          center: [tLng, tLat] as [number, number],
        };
      });

      out.push(new PolygonLayer({
        id: 'balli-impact-outer',
        data: impactData, pickable: true,
        getPolygon: (d) => d.polygon,
        getFillColor: (d) => selOrColor(selectedIds.has(d.feature.properties?.id || ''), d.feature.properties?.style?.fillColor || '#FF4444', 15),
        getLineColor: (d) => selOrColor(selectedIds.has(d.feature.properties?.id || ''), d.feature.properties?.style?.strokeColor || '#FF4444', 100),
        getLineWidth: 1.5, lineWidthMinPixels: 1, stroked: true, filled: true,
        updateTriggers: { getLineColor: [selectedIds], getFillColor: [selectedIds] },
      }));
      out.push(new PolygonLayer({
        id: 'balli-impact-inner',
        data: impactData, pickable: false,
        getPolygon: (d) => d.innerPolygon,
        getFillColor: [0, 0, 0, 0],
        getLineColor: (d) => selOrColor(selectedIds.has(d.feature.properties?.id || ''), d.feature.properties?.style?.strokeColor || '#FF4444', 45),
        getLineWidth: 1, lineWidthMinPixels: 1, stroked: true, filled: false,
        updateTriggers: { getLineColor: [selectedIds] },
      }));

      // Target crosshairs
      const targets = impactData.map((d) => ({
        position: d.center,
        color: [255, 70, 70, 255] as [number, number, number, number],
      }));
      pushMarkerBatch(out, 'balli-target', targets);
    }

    return out;
  }, [visibleAnnotations, selectedIds, drawingPreview, drawingStyle, selectionBox, ballisticFeatures]);

  // ── ANIMATED LAYERS (ballistic dots only, ~7fps) ───────────────
  const animatedLayers = useMemo(() => {
    const result: DeckLayer[] = [];
    const phase = phaseRef.current;

    if (ballisticFeatures.length > 0) {
      const allDots: { position: [number, number]; color: [number, number, number, number] }[] = [];
      for (const f of ballisticFeatures) {
        const traj = (f.geometry as LineString).coordinates;
        const dots = sampleBallisticDots(traj, 12, phase);
        const sel = selectedIds.has(f.properties?.id || '');
        const col = sel
          ? [SEL[0], SEL[1], SEL[2], 255] as [number, number, number, number]
          : hexToRgba(f.properties?.style?.strokeColor || '#FF4444', 255);
        for (const d of dots) allDots.push({ position: d as [number, number], color: col });
      }
      result.push(new ScatterplotLayer({
        id: 'balli-dots',
        data: allDots, pickable: false,
        getPosition: (d) => d.position,
        getFillColor: (d) => d.color,
        getRadius: 3.5, radiusMinPixels: 2, radiusMaxPixels: 5,
      }));
    }

    if (drawingPreview?.type === 'ballistic' && drawingPreview.coordinates.length >= 2) {
      const si = drawingPreview.coordinates.findIndex((c) => c[0] === -999 && c[1] === -999);
      const traj = si !== -1 ? drawingPreview.coordinates.slice(0, si) : drawingPreview.coordinates;
      if (traj.length >= 2) {
        const dots = sampleBallisticDots(traj, 10, phase);
        result.push(new ScatterplotLayer({
          id: 'preview-balli-dots',
          data: dots,
          getPosition: (d: [number, number]) => d,
          getFillColor: hexToRgba(drawingStyle.color, 255),
          getRadius: 3.5, radiusMinPixels: 2, radiusMaxPixels: 5,
        }));
      }
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotTick, ballisticFeatures, selectedIds, drawingPreview, drawingStyle]);

  const layers = useMemo(
    () => [...staticLayers, ...animatedLayers],
    [staticLayers, animatedLayers]
  );

  const handleClick = useCallback((info: PickingInfo, event: { srcEvent: MouseEvent }) => {
    const feature = info.object as OperationalFeature | undefined;
    if (feature?.properties?.id) {
      toggleSelection(feature.properties.id, event.srcEvent.shiftKey);
    }
  }, [toggleSelection]);

  const handleHover = useCallback((_info: PickingInfo) => {}, []);

  return { layers, handleClick, handleHover };
}

// ── Marker helpers ───────────────────────────────────────────────
// Reusable "ring + dot" marker pattern

function pushMarker(
  out: DeckLayer[],
  idPrefix: string,
  data: [number, number][],
  color: [number, number, number, number],
) {
  out.push(
    new ScatterplotLayer({
      id: `${idPrefix}-ring`,
      data,
      getPosition: (d: [number, number]) => d,
      getRadius: 10, getFillColor: [0, 0, 0, 0],
      getLineColor: color, getLineWidth: 2, stroked: true,
      radiusMinPixels: 8, radiusMaxPixels: 14,
    }),
    new ScatterplotLayer({
      id: `${idPrefix}-dot`,
      data,
      getPosition: (d: [number, number]) => d,
      getRadius: 4, getFillColor: color,
      radiusMinPixels: 3, radiusMaxPixels: 6,
    }),
  );
}

function pushMarkerBatch(
  out: DeckLayer[],
  idPrefix: string,
  data: { position: [number, number]; color: [number, number, number, number] }[],
) {
  out.push(
    new ScatterplotLayer({
      id: `${idPrefix}-ring`,
      data, pickable: false,
      getPosition: (d) => d.position,
      getRadius: 10, getFillColor: [0, 0, 0, 0],
      getLineColor: (d) => d.color, getLineWidth: 2, stroked: true,
      radiusMinPixels: 8, radiusMaxPixels: 14,
    }),
    new ScatterplotLayer({
      id: `${idPrefix}-dot`,
      data, pickable: false,
      getPosition: (d) => d.position,
      getRadius: 4, getFillColor: (d) => d.color,
      radiusMinPixels: 3, radiusMaxPixels: 6,
    }),
  );
}
